import { useContext } from "react";
import PropTypes from "prop-types";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import "./SearchButton.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import SeparatorLine from "app/components/ui/Separator/SeparatorLine";
import { FormattedMessage, FormattedPlural, FormattedDate } from "react-intl";
import Button from "app/pages/.shared/form/Button";
import classNames from "classnames";
import { isSameMonth } from "date-fns";
import PenFilledIcon from "app/icons/navigation/PenFilledIcon";
import { ICON_SIZES } from "app/constants";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";

const RouteSection = ({ from, stopover, to, isBold = false }) => {
	const fromClassName = classNames("search-button__city-from", {
		"search-button__city-from--ellipsis": from?.length > 10,
	});

	const stopoverClassName = classNames("search-button__city-stopover", {
		"search-button__city-stopover--ellipsis": stopover?.length > 10,
	});

	const toClassName = classNames("search-button__city-to", {
		"search-button__city-to--ellipsis": to?.length > 10,
	});

	return (
		<>
			{from?.length > 0 && (
				<>
					<Typography
						component="span"
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className={fromClassName}
						isBold={isBold}
					>
						{from}
					</Typography>

					<Typography
						component="span"
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="search-button__city-separator"
						isBold={isBold}
					>
						{" - "}
					</Typography>
				</>
			)}

			{stopover?.length > 0 && (
				<>
					<Typography
						component="span"
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className={stopoverClassName}
						isBold={isBold}
						aria-hidden="true"
					>
						{stopover}
					</Typography>

					<Typography
						component="span"
						variant={TYPOGRAPHY_VARIANTS.REGULAR}
						className="search-button__city-separator"
						isBold={isBold}
					>
						{" - "}
					</Typography>
				</>
			)}

			<Typography
				component="span"
				variant={TYPOGRAPHY_VARIANTS.REGULAR}
				className={toClassName}
				isBold={isBold}
			>
				{to || <LoaderBar width={60} height={14} />}
			</Typography>
		</>
	);
};

const DetailsSection = ({ fromDate, toDate, travelerCount, roomCount, isMobile = false }) => (
	<>
		<Typography
			component="span"
			variant={TYPOGRAPHY_VARIANTS.REGULAR}
			className="search-button__dates"
			aria-hidden="true"
		>
			<FormattedDate
				value={fromDate}
				day="numeric"
				{...(isSameMonth(fromDate, toDate) ? undefined : { month: "short" })}
			/>{" "}
			- <FormattedDate value={toDate} day="numeric" month="short" />
		</Typography>

		{isMobile ? "・" : <SeparatorLine vertical />}

		<Typography
			component="span"
			variant={TYPOGRAPHY_VARIANTS.REGULAR}
			className="search-button__travelers-and-rooms"
			aria-hidden="true"
		>
			<FormattedMessage id="traveler.count" values={{ count: travelerCount }} />,{" "}
			<FormattedPlural
				value={roomCount}
				one={
					<FormattedMessage
						values={{ count: roomCount }}
						id="general.room.count.label.singular"
					/>
				}
				other={
					<FormattedMessage
						values={{ count: roomCount }}
						id="general.room.count.label.plural"
					/>
				}
			/>
		</Typography>
	</>
);

const SearchButton = ({ onClick, itinerary }) => {
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	return (
		<Button
			className="search-button"
			onClick={onClick}
			aria-haspopup="true"
			type="button"
			data-testid="search-button"
		>
			{isMobile ? (
				<div className="search-button__itinerary">
					<div className="search-button__route">
						<RouteSection
							from={itinerary.from}
							stopover={itinerary.stopover}
							to={itinerary.to}
							isBold
						/>
					</div>

					<div className="search-button__details">
						<DetailsSection
							fromDate={itinerary.dates.from}
							toDate={itinerary.dates.to}
							travelerCount={itinerary.travelerCount}
							roomCount={itinerary.roomCount}
							isMobile={isMobile}
						/>
					</div>
				</div>
			) : (
				<>
					<RouteSection
						from={itinerary.from}
						stopover={itinerary.stopover}
						to={itinerary.to}
					/>

					<SeparatorLine vertical />

					<DetailsSection
						fromDate={itinerary.dates.from}
						toDate={itinerary.dates.to}
						travelerCount={itinerary.travelerCount}
						roomCount={itinerary.roomCount}
						isMobile={isMobile}
					/>
				</>
			)}

			<PenFilledIcon
				className="search-button__edit-icon"
				focusable="false"
				aria-hidden="true"
				size={ICON_SIZES.LG}
			/>
		</Button>
	);
};

RouteSection.propTypes = {
	from: PropTypes.string,
	stopover: PropTypes.string,
	to: PropTypes.string.isRequired,
	isBold: PropTypes.bool,
};

DetailsSection.propTypes = {
	fromDate: PropTypes.string.isRequired,
	toDate: PropTypes.string.isRequired,
	travelerCount: PropTypes.number.isRequired,
	roomCount: PropTypes.number.isRequired,
	isMobile: PropTypes.bool,
};

SearchButton.propTypes = {
	onClick: PropTypes.func.isRequired,
	itinerary: PropTypes.shape({
		from: PropTypes.string,
		to: PropTypes.string.isRequired,
		stopover: PropTypes.string,
		dates: PropTypes.shape({
			from: PropTypes.number.isRequired,
			to: PropTypes.number.isRequired,
		}).isRequired,
		travelerCount: PropTypes.number.isRequired,
		roomCount: PropTypes.number.isRequired,
	}).isRequired,
};

export default SearchButton;
